import { Button } from '@ads/components-react';

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddLanguageForm,
  type EditingLang,
  ExamDetailTabs,
  type ExamEditTab,
  ExamEditTemplate,
  ExamForm,
  ExamLanguageList,
  type ExamPublication,
  ImportFromLibraryModal,
  PublicationList,
  QuestionList,
  type SelectableQuestion,
  useExamDetail,
  useExamForm,
  useExamQuestions,
} from '@/features/exam-management';
import { usePublicationManagement } from '@/features/exam-management/hooks/usePublicationManagement';
import { useToggleExamLangStatus } from '@/features/exam-management/hooks/useToggleExamLangStatus';
import { AddStudentsBatchModal } from '@/features/exam-management/ui/organisms/AddStudentsBatchModal';
import { EditPublicationModal } from '@/features/exam-management/ui/organisms/EditPublicationModal';
import { ExpandDurationModal } from '@/features/exam-management/ui/organisms/ExpandDurationModal';
import { Spinner } from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';
import { cn } from '@/shared/utils/cn';

export const Route = createFileRoute('/_authenticated/exams/$id/' as never)({
  component: ExamDetailPage,
});

function ExamDetailPage() {
  const { id } = (Route as unknown as { useParams: () => { id: string } }).useParams();
  const { t } = useTranslation('examManagement');
  const { exam, languages, activeTab, setActiveTab, isLoading } = useExamDetail(id);
  const { form, updateForm, onSubmit, isSubmitting } = useExamForm(id);

  // ─── State for language editing ─────────────────────────────────────────────
  const [editingLang, setEditingLang] = useState<EditingLang | null>(null);
  const { toggleStatus, pendingId: togglePendingId } = useToggleExamLangStatus(Number(id));

  // Rola até o formulário "Adicionar idioma" (posicionado abaixo da lista) ao
  // entrar em modo edição, para o admin não perder o formulário de vista.
  const addLanguageFormRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editingLang !== null) {
      addLanguageFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingLang]);

  // ─── State for forms ───────────────────────────────────────────────────────
  const [isImportLibraryOpen, setIsImportLibraryOpen] = useState(false);

  // ─── Publication modal state ──────────────────────────────────────────────
  const [editPub, setEditPub] = useState<ExamPublication | null>(null);
  const [expandPub, setExpandPub] = useState<ExamPublication | null>(null);
  const [addStudentsPub, setAddStudentsPub] = useState<ExamPublication | null>(null);

  const pubManagement = usePublicationManagement(addStudentsPub?.id ?? 0);

  // ─── Language tabs for questions ───────────────────────────────────────────
  const [selectedQuestionsLangTab, setSelectedQuestionsLangTab] = useState<number>(0);
  const currentLang = languages[selectedQuestionsLangTab] ?? null;
  const { questions: currentLangQuestions, isLoading: isLoadingQuestions } = useExamQuestions(
    currentLang?.id ?? 0,
  );

  const examCultureInfos = useMemo(
    () => languages.map(l => l.cultureInfo).filter((c): c is string => Boolean(c)),
    [languages],
  );
  const examLangIdByCultureInfo = useMemo(
    () =>
      Object.fromEntries(
        languages
          .filter(l => l.cultureInfo && l.id)
          .map(l => [l.cultureInfo as string, l.id as number]),
      ),
    [languages],
  );
  const existingQuestionIds = useMemo(
    () => currentLangQuestions.map(q => q.id ?? 0),
    [currentLangQuestions],
  );

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleImportFromLibrary = useCallback(
    (importedQuestions: readonly SelectableQuestion[]) => {
      const targetLangId = currentLang?.id ?? languages[0]?.id;
      if (!targetLangId) return;
      void importedQuestions;
      setIsImportLibraryOpen(false);
    },
    [currentLang, languages],
  );

  const handleTabChange = (tab: ExamEditTab) => {
    setActiveTab(tab);
    setEditingLang(null);
    setSelectedQuestionsLangTab(0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-xxl">
        <Spinner />
      </div>
    );
  }

  if (!exam) {
    return <EmptyState title={t('table.empty')} />;
  }

  return (
    <ExamEditTemplate
      exam={exam}
      tabs={
        <ExamDetailTabs activeTab={activeTab} onTabChange={handleTabChange}>
          {/** ── General Data Tab ─────────────────────────────── */}
          {activeTab === 'general' && (
            <div className="flex flex-col gap-md">
              <ExamForm form={form} onUpdate={updateForm} showInstruction={false} />

              <div className="flex justify-end gap-sm">
                <Button hierarchy="primary" size="md" isLoading={isSubmitting} onClick={onSubmit}>
                  {t('actions.saveChanges')}
                </Button>
              </div>
            </div>
          )}

          {/** ── Languages Tab ────────────────────────────────── */}
          {activeTab === 'languages' && (
            <div className="flex flex-col gap-mini">
              {languages.length > 0 && (
                <ExamLanguageList
                  languages={languages}
                  isLoading={isLoading}
                  {...(editingLang !== null ? { editingLangId: editingLang.id } : {})}
                  onEdit={lang =>
                    setEditingLang({
                      id: lang.id ?? 0,
                      cultureInfo: lang.cultureInfo ?? '',
                      active: lang.active ?? false,
                    })
                  }
                  onToggleStatus={toggleStatus}
                  pendingToggleId={togglePendingId}
                />
              )}

              <div ref={addLanguageFormRef} className="scroll-mt-mini">
                <AddLanguageForm
                  examId={Number(id)}
                  {...(editingLang !== null ? { editingLang } : {})}
                  onSave={() => setEditingLang(null)}
                  onCancel={() => setEditingLang(null)}
                />
              </div>
            </div>
          )}

          {/** ── Questions Tab (with auto-translation) ────────── */}
          {activeTab === 'questions' && (
            <div className="flex flex-col gap-mini">
              <h3 className="ts-subtitle-sm text-ctx-content-title">{t('questions.title')}</h3>
              {languages.length > 0 && (
                <>
                  <div className="flex gap-mini w-full">
                    {languages.map((lang, idx) => (
                      <button
                        key={lang.id}
                        type="button"
                        onClick={() => setSelectedQuestionsLangTab(idx)}
                        className={cn(
                          'flex flex-1 items-center justify-center bg-ctx-layout-spotlight hover:bg-ctx-interactive-secondaryHover text-ctx-content-base ts-paragraph-xxs rounded-sm !border-hairline border-ctx-layout-border p-mini',
                          idx === selectedQuestionsLangTab
                            ? 'border-ctx-content-base bg-ctx-layout-body text-ctx-content-title'
                            : '',
                        )}
                      >
                        {lang.cultureInfoDisplay}
                      </button>
                    ))}
                  </div>

                  {currentLang && (
                    <div className="flex flex-col gap-md">
                      <div className="flex justify-end gap-mini">
                        <Button
                          hierarchy="secondary"
                          size="md"
                          onClick={() => setIsImportLibraryOpen(true)}
                        >
                          {t('actions.importFromLibrary')}
                        </Button>
                      </div>
                      <ImportFromLibraryModal
                        open={isImportLibraryOpen}
                        examLangId={currentLang.id ?? 0}
                        {...(currentLang.cultureInfo
                          ? { initialCultureInfo: currentLang.cultureInfo }
                          : {})}
                        existingQuestionIds={existingQuestionIds}
                        examCultureInfos={examCultureInfos}
                        examLangIdByCultureInfo={examLangIdByCultureInfo}
                        onImport={handleImportFromLibrary}
                        onClose={() => setIsImportLibraryOpen(false)}
                      />
                      <QuestionList
                        questions={currentLangQuestions}
                        isLoading={isLoadingQuestions}
                        navigateToEdit
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/** ── Publications Tab ─────────────────────────────── */}
          {activeTab === 'publications' && (
            <PublicationList
              examId={Number(id)}
              onEditPublication={pub => setEditPub(pub)}
              onExpandDuration={pub => setExpandPub(pub)}
              onAddStudents={pub => setAddStudentsPub(pub)}
              onDeletePublication={_pub => {}}
              onRemoveStudent={(_pubId, _studentId) => {}}
            />
          )}
        </ExamDetailTabs>
      }
    >
      <EditPublicationModal
        publication={editPub}
        open={editPub !== null}
        onClose={() => setEditPub(null)}
        onSave={_data => {
          setEditPub(null);
        }}
      />

      <ExpandDurationModal
        publication={expandPub}
        open={expandPub !== null}
        onClose={() => setExpandPub(null)}
        onSave={_data => {
          setExpandPub(null);
        }}
      />

      <AddStudentsBatchModal
        publication={addStudentsPub}
        availableStudents={pubManagement.availableStudents}
        open={addStudentsPub !== null}
        onClose={() => setAddStudentsPub(null)}
        onConfirm={_data => {
          setAddStudentsPub(null);
        }}
      />
    </ExamEditTemplate>
  );
}
